import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { ZoomIn, ZoomOut } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface ImageCropDialogProps {
  open: boolean;
  onClose: () => void;
  imageSrc: string;
  onCropComplete: (croppedImage: Blob) => void;
  aspectRatio?: number;
  cropShape?: 'rect' | 'round';
}

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });

async function getCroppedImg(
  imageSrc: string,
  pixelCrop: CropArea
): Promise<Blob> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas is empty'));
        return;
      }
      resolve(blob);
    }, 'image/jpeg', 0.95);
  });
}

export const ImageCropDialog: React.FC<ImageCropDialogProps> = ({
  open,
  onClose,
  imageSrc,
  onCropComplete,
  aspectRatio = 16 / 9,
  cropShape = 'rect'
}) => {
  const { language, t } = useLanguage();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const onCropAreaComplete = useCallback((croppedArea: any, croppedAreaPixels: CropArea) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropConfirm = useCallback(async () => {
    if (!croppedAreaPixels) return;
    
    setIsProcessing(true);
    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
      onCropComplete(croppedImage);
      onClose();
    } catch (error) {
      console.error('Error cropping image:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [croppedAreaPixels, imageSrc, onCropComplete, onClose]);

  // Ne pas afficher le dialogue si l'image n'est pas définie
  if (!imageSrc) return null;

  return (
    <Dialog open={open} onOpenChange={onClose} modal={true}>
      <DialogContent 
        className="bg-slate-900 border-slate-700 max-w-4xl h-[80vh] flex flex-col !z-[10002]"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => {
          if (!isProcessing) {
            onClose();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-white">{t('imageCrop.title')}</DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 relative bg-slate-950 rounded-lg overflow-hidden">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio}
            cropShape={cropShape}
            onCropChange={setCrop}
            onCropComplete={onCropAreaComplete}
            onZoomChange={setZoom}
            restrictPosition={false}
            showGrid={true}
            style={{
              containerStyle: {
                backgroundColor: 'rgb(2, 6, 23)'
              },
              cropAreaStyle: {
                border: '2px solid rgba(139, 92, 246, 0.5)',
              }
            }}
          />
        </div>

        <div className="space-y-4 pt-4">
          <div className="flex items-center gap-4">
            <ZoomOut className="w-4 h-4 text-slate-400" />
            <Slider
              value={[zoom]}
              onValueChange={(value) => setZoom(value[0])}
              min={1}
              max={3}
              step={0.1}
              className="flex-1"
            />
            <ZoomIn className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-400 min-w-[60px] text-right">
              {Math.round(zoom * 100)}%
            </span>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isProcessing}
            className="border-slate-600 text-slate-300 hover:bg-slate-800"
          >
            {t('imageCrop.cancel')}
          </Button>
          <Button
            onClick={handleCropConfirm}
            disabled={isProcessing}
            className="bg-gaming-primary hover:bg-gaming-primary/80"
          >
            {isProcessing ? t('imageCrop.processing') : t('imageCrop.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};